import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Mail,
  TrendingUp,
  BookOpen,
  Users,
  Calendar,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  Star,
} from "lucide-react";
import type { Term, AdminStats, AdminView } from "../types";
import { getStatusBadge } from "../utils";

interface AdminReportsProps {
  terms: Term[];
  stats: AdminStats;
  alphabetCounts: [string, number][];
  recentTermsByMonth: { month: string; count: number }[];
  onViewChange: (view: AdminView) => void;
}

interface ContributorInfo {
  name: string;
  email: string | null;
  count: number;
  pending: number;
  approved: number;
  rejected: number;
  lastSubmission: string;
}

export function AdminReports({
  terms,
  stats,
  alphabetCounts,
  recentTermsByMonth,
  onViewChange,
}: AdminReportsProps) {
  // Contributor analysis
  const contributorMap: Record<string, ContributorInfo> = {};
  terms.forEach(term => {
    const key = term.contributor_email || term.contributor_name || null;
    if (!key) {
      return;
    }
    if (!contributorMap[key]) {
      contributorMap[key] = {
        name: term.contributor_name || term.contributor_email || "Unknown",
        email: term.contributor_email || null,
        count: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        lastSubmission: term.created_at,
      };
    }
    contributorMap[key].count += 1;
    contributorMap[key][term.status] += 1;
    if (term.created_at > contributorMap[key].lastSubmission) {
      contributorMap[key].lastSubmission = term.created_at;
    }
  });
  const sortedContributors = Object.values(contributorMap).sort(
    (a, b) => b.count - a.count
  );

  // Monthly trend (stacked)
  const monthlyData: Record<
    string,
    { pending: number; approved: number; rejected: number }
  > = {};
  terms.forEach(term => {
    const month = new Date(term.created_at).toLocaleDateString("en-US", {
      month: "short",
      year: "2-digit",
    });
    if (!monthlyData[month]) {
      monthlyData[month] = { pending: 0, approved: 0, rejected: 0 };
    }
    monthlyData[month][term.status] += 1;
  });
  const monthlyEntries = Object.entries(monthlyData).slice(-6);

  // Stats
  const totalReviewed = stats.approved + stats.rejected;
  const approvalRate =
    totalReviewed > 0 ? Math.round((stats.approved / totalReviewed) * 100) : 0;
  const featuredCount = terms.filter(t => t.featured).length;

  // Language coverage
  const approvedTerms = terms.filter(t => t.status === "approved");
  const withEnglish = approvedTerms.filter(t => t.meaning_english).length;
  const withHindi = approvedTerms.filter(t => t.meaning_hindi).length;
  const withGujarati = approvedTerms.filter(t => t.meaning_gujarati).length;
  const withAllThree = approvedTerms.filter(
    t => t.meaning_english && t.meaning_hindi && t.meaning_gujarati
  ).length;

  // Pie data
  const pieData = [
    { name: "Pending", value: stats.pending, color: "#eab308" },
    { name: "Approved", value: stats.approved, color: "#22c55e" },
    { name: "Rejected", value: stats.rejected, color: "#ef4444" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-1 font-serif text-2xl font-bold">
          Reports & Dashboard
        </h2>
        <p className="text-muted-foreground">
          Complete overview of the Jain Lexicon
        </p>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Card
          className="cursor-pointer border-yellow-200 bg-yellow-50/50 transition-shadow hover:shadow-md"
          onClick={() => onViewChange("contributions")}
        >
          <CardContent className="p-4 text-center">
            <Clock className="mx-auto mb-1 h-5 w-5 text-yellow-600" />
            <p className="text-3xl font-bold text-yellow-800">
              {stats.pending}
            </p>
            <p className="text-muted-foreground text-xs">Pending</p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer border-green-200 bg-green-50/50 transition-shadow hover:shadow-md"
          onClick={() => onViewChange("contributions")}
        >
          <CardContent className="p-4 text-center">
            <CheckCircle className="mx-auto mb-1 h-5 w-5 text-green-600" />
            <p className="text-3xl font-bold text-green-800">
              {stats.approved}
            </p>
            <p className="text-muted-foreground text-xs">Approved</p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer border-red-200 bg-red-50/50 transition-shadow hover:shadow-md"
          onClick={() => onViewChange("contributions")}
        >
          <CardContent className="p-4 text-center">
            <XCircle className="mx-auto mb-1 h-5 w-5 text-red-600" />
            <p className="text-3xl font-bold text-red-800">{stats.rejected}</p>
            <p className="text-muted-foreground text-xs">Rejected</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <BookOpen className="text-primary mx-auto mb-1 h-5 w-5" />
            <p className="text-3xl font-bold">{terms.length}</p>
            <p className="text-muted-foreground text-xs">Total Terms</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <TrendingUp className="mx-auto mb-1 h-5 w-5 text-green-600" />
            <p className="text-3xl font-bold text-green-700">{approvalRate}%</p>
            <p className="text-muted-foreground text-xs">Approval Rate</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <Star className="mx-auto mb-1 h-5 w-5 text-yellow-500" />
            <p className="text-3xl font-bold text-yellow-600">
              {featuredCount}
            </p>
            <p className="text-muted-foreground text-xs">Featured</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 overflow-x-auto pt-0">
            <div className="mx-auto h-[280px] w-full min-w-[260px]">
              <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={95}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monthly Submissions</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 overflow-x-auto pt-0">
            <div className="mx-auto h-[280px] w-full min-w-[260px]">
              <ResponsiveContainer width="100%" height="100%">
              <BarChart data={recentTermsByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="count"
                  fill="hsl(var(--primary))"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Language Coverage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Language Coverage (Approved Terms)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="bg-secondary/30 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold">{withEnglish}</p>
              <p className="text-muted-foreground text-sm">English</p>
              <p className="text-primary text-xs">
                {approvedTerms.length > 0
                  ? Math.round((withEnglish / approvedTerms.length) * 100)
                  : 0}
                %
              </p>
            </div>
            <div className="bg-secondary/30 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold">{withHindi}</p>
              <p className="text-muted-foreground text-sm">Hindi</p>
              <p className="text-primary text-xs">
                {approvedTerms.length > 0
                  ? Math.round((withHindi / approvedTerms.length) * 100)
                  : 0}
                %
              </p>
            </div>
            <div className="bg-secondary/30 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold">{withGujarati}</p>
              <p className="text-muted-foreground text-sm">Gujarati</p>
              <p className="text-primary text-xs">
                {approvedTerms.length > 0
                  ? Math.round((withGujarati / approvedTerms.length) * 100)
                  : 0}
                %
              </p>
            </div>
            <div className="bg-primary/10 border-primary/20 rounded-xl border p-4 text-center">
              <p className="text-primary text-2xl font-bold">{withAllThree}</p>
              <p className="text-muted-foreground text-sm">All 3 Languages</p>
              <p className="text-primary text-xs">
                {approvedTerms.length > 0
                  ? Math.round((withAllThree / approvedTerms.length) * 100)
                  : 0}
                %
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trend Bar */}
      {monthlyEntries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="text-primary h-5 w-5" /> Monthly Status
              Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {monthlyEntries.map(([month, data]) => {
                const total = data.pending + data.approved + data.rejected;
                return (
                  <div
                    key={month}
                    className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4"
                  >
                    <span className="shrink-0 text-sm font-medium sm:w-20">
                      {month}
                    </span>
                    <div className="flex h-8 min-w-0 flex-1 items-center gap-1">
                      {data.approved > 0 && (
                        <div
                          className="h-full rounded-l bg-green-500"
                          style={{ width: `${(data.approved / total) * 100}%` }}
                          title={`${data.approved} approved`}
                        />
                      )}
                      {data.pending > 0 && (
                        <div
                          className="h-full bg-yellow-500"
                          style={{ width: `${(data.pending / total) * 100}%` }}
                          title={`${data.pending} pending`}
                        />
                      )}
                      {data.rejected > 0 && (
                        <div
                          className="h-full rounded-r bg-red-500"
                          style={{ width: `${(data.rejected / total) * 100}%` }}
                          title={`${data.rejected} rejected`}
                        />
                      )}
                    </div>
                    <span className="shrink-0 text-right text-sm font-bold sm:w-8">
                      {total}
                    </span>
                  </div>
                );
              })}
              <div className="text-muted-foreground mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs">
                <span className="flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded bg-green-500" />{" "}
                  Approved
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded bg-yellow-500" />{" "}
                  Pending
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded bg-red-500" />{" "}
                  Rejected
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Submissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="text-primary h-5 w-5" /> Recent Submissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {terms.slice(0, 8).map(term => (
            <div
              key={term.id}
              className="border-border/40 flex flex-col gap-2 border-b py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="font-semibold break-words">{term.word}</p>
                <p className="text-muted-foreground text-sm break-words">
                  {term.contributor_name || term.contributor_email || "-"} •{" "}
                  {new Date(term.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="shrink-0">{getStatusBadge(term.status)}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Alphabetical Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Alphabetical Distribution (Approved)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alphabetCounts.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center">
              No approved terms yet
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-7 md:grid-cols-9">
              {alphabetCounts.map(([letter, count]) => (
                <div
                  key={letter}
                  className="bg-secondary/30 hover:bg-primary/10 rounded-lg p-3 text-center transition-colors"
                >
                  <p className="text-lg font-bold">{letter}</p>
                  <p className="text-muted-foreground text-sm">{count}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Contributors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="text-primary h-5 w-5" /> Top Contributors
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sortedContributors.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center">
              No contributors yet
            </p>
          ) : (
            sortedContributors.slice(0, 15).map((contributor, i) => (
              <div
                key={contributor.name + i}
                className="border-border/40 flex flex-col gap-3 border-b py-3 last:border-0 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="bg-primary/10 text-primary flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <span className="block break-words font-medium">
                      {contributor.name}
                    </span>
                    {contributor.email && (
                      <a
                        href={`mailto:${contributor.email}`}
                        className="text-primary mt-0.5 flex flex-wrap items-center gap-1 break-all text-sm hover:underline"
                      >
                        <Mail className="h-3.5 w-3.5" /> {contributor.email}
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="bg-green-100 text-green-800"
                  >
                    {contributor.approved} ✓
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="bg-yellow-100 text-yellow-800"
                  >
                    {contributor.pending} ⏳
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="bg-red-100 text-red-800"
                  >
                    {contributor.rejected} ✗
                  </Badge>
                  <Badge variant="outline" className="font-bold">
                    {contributor.count}
                  </Badge>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
